import { useState, useRef, useEffect, JSX } from "react";
// Material UI icon imports
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import FastForwardIcon from "@mui/icons-material/FastForward";
import FastRewindIcon from "@mui/icons-material/FastRewind";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import FullscreenExitIcon from "@mui/icons-material/FullscreenExit";
import Close from "../../images/Close.svg";
interface VideoPlayerProps {
  videoSrc?: string;
  posterSrc?: string;
  setModal: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function VideoPlayer({
  videoSrc,
  setModal,
  posterSrc = "/api/placeholder/640/360"
}: VideoPlayerProps): JSX.Element {
  // State for video player
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [progress, setProgress] = useState<number>(0);
  const [currentTime, setCurrentTime] = useState<number>(0);
  const [duration, setDuration] = useState<number>(0);
  const [volume, setVolume] = useState<number>(1);
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isFullscreen, setIsFullscreen] = useState<boolean>(false);
  const [showControls, setShowControls] = useState<boolean>(true);

  // References
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Format time in mm:ss format
  const formatTime = (timeInSeconds: number): string => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Play/Pause toggle
  const togglePlay = (): void => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };

  // Skip forward 10 seconds
  const skipForward = (): void => {
    if (!videoRef.current) return;
    videoRef.current.currentTime += 10;
  };

  // Skip backward 10 seconds
  const skipBackward = (): void => {
    if (!videoRef.current) return;
    videoRef.current.currentTime -= 10;
  };

  // Handle volume change
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    if (!videoRef.current) return;

    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    videoRef.current.volume = newVolume;
    setIsMuted(newVolume === 0);
  };

  // Toggle mute
  const toggleMute = (): void => {
    if (!videoRef.current) return;

    if (isMuted) {
      videoRef.current.volume = volume || 0.5;
      setIsMuted(false);
      if (volume === 0) setVolume(0.5);
    } else {
      videoRef.current.volume = 0;
      setIsMuted(true);
    }
  };

  // Handle progress bar change
  const handleProgressChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ): void => {
    if (!videoRef.current) return;

    const newTime = (parseFloat(e.target.value) / 100) * duration;
    setProgress(parseFloat(e.target.value));
    videoRef.current.currentTime = newTime;
  };

  // Time update event handler
  const handleTimeUpdate = (): void => {
    if (!videoRef.current) return;

    const newProgress =
      (videoRef.current.currentTime / videoRef.current.duration) * 100;
    setProgress(newProgress);
    setCurrentTime(videoRef.current.currentTime);
  };

  // Duration change event handler
  const handleDurationChange = (): void => {
    if (!videoRef.current) return;
    setDuration(videoRef.current.duration);
  };

  // Toggle fullscreen
  const toggleFullscreen = (): void => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  // Handle fullscreen change
  useEffect(() => {
    const handleFullscreenChange = (): void => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Auto-hide controls after inactivity
  const resetControlsTimeout = (): void => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    setShowControls(true);

    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) {
        setShowControls(false);
      }
    }, 3000);
  };

  // Set up event listeners
  useEffect(() => {
    const videoElement = videoRef.current;
    const containerElement = containerRef.current;

    if (videoElement && containerElement) {
      videoElement.addEventListener("timeupdate", handleTimeUpdate);
      videoElement.addEventListener("durationchange", handleDurationChange);
      videoElement.addEventListener("ended", () => setIsPlaying(false));

      // Event to show controls on mouse move
      containerElement.addEventListener("mousemove", resetControlsTimeout);
    }

    // Initialize controls timeout
    resetControlsTimeout();

    return () => {
      if (videoElement) {
        videoElement.removeEventListener("timeupdate", handleTimeUpdate);
        videoElement.removeEventListener(
          "durationchange",
          handleDurationChange
        );
        videoElement.removeEventListener("ended", () => setIsPlaying(false));
      }

      if (containerElement) {
        containerElement.removeEventListener("mousemove", resetControlsTimeout);
      }

      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying]);

  return (
    <>
      <div className="video-preview">
        <div className="video-preview__image">
          <img
            src={Close}
            alt="
        "
            onClick={() => setModal(false)}
          />
        </div>
        <div
          ref={containerRef}
          className="relative w-full max-w-3xl mx-auto bg-black rounded-lg overflow-hidden video-preview__player"
          onMouseEnter={() => setShowControls(true)}
          onMouseLeave={() => isPlaying && setShowControls(false)}
        >
          {/* Video Element */}
          <video
            ref={videoRef}
            className=" video-preview__player--view"
            onClick={togglePlay}
            // poster={posterSrc}
          >
            <source src={videoSrc}></source>
          </video>

          {/* Video Controls - show/hide based on state */}
          <div
            className={` transition-opacity duration-300  ${
              showControls ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="video-preview__bottom">
              <div className="video-preview__volume">
                {/* Volume Control */}
                <button
                  onClick={toggleMute}
                  className="text-white hover:text-blue-400 transition-colors"
                  aria-label={isMuted ? "Unmute" : "Mute"}
                >
                  {isMuted ? (
                    <VolumeOffIcon fontSize="small" />
                  ) : (
                    <VolumeUpIcon fontSize="small" />
                  )}
                </button>

                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.1"
                  value={isMuted ? 0 : volume}
                  onChange={handleVolumeChange}
                  className="w-16 h-1 bg-gray-400 rounded-lg appearance-none cursor-pointer"
                />
              </div>
              <div className="video-preview__controls">
                <button
                  onClick={skipBackward}
                  className="text-white hover:text-blue-400 transition-colors"
                  aria-label="Skip backward"
                >
                  <FastRewindIcon fontSize="small" />
                </button>

                <button
                  onClick={togglePlay}
                  className="text-white hover:text-blue-400 transition-colors"
                  aria-label={isPlaying ? "Pause" : "Play"}
                >
                  {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
                </button>

                <button
                  onClick={skipForward}
                  className="text-white hover:text-blue-400 transition-colors"
                  aria-label="Skip forward"
                >
                  <FastForwardIcon fontSize="small" />
                </button>
              </div>

              <div className="video-preview__full-screen">
                {/* Fullscreen Button */}
                <button
                  onClick={toggleFullscreen}
                  className="text-white hover:text-blue-400 transition-colors"
                  aria-label={
                    isFullscreen ? "Exit fullscreen" : "Enter fullscreen"
                  }
                >
                  {isFullscreen ? (
                    <FullscreenExitIcon fontSize="small" />
                  ) : (
                    <FullscreenIcon fontSize="small" />
                  )}
                </button>
                {/* Play/Pause overlay button in the center of the video */}
                {!isPlaying && (
                  <button
                    onClick={togglePlay}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white/30 hover:bg-white/50 rounded-full  transition-colors"
                    aria-label="Play"
                  >
                    <PlayArrowIcon
                      className="text-white"
                      style={{ fontSize: "25" }}
                    />
                  </button>
                )}
              </div>
            </div>
            {/* Progress Bar */}
            <div className="video-preview__progress-bar">
              <span className="text-white text-xs">
                {formatTime(currentTime)}
              </span>
              <input
                type="range"
                min="0"
                max="100"
                value={progress}
                onChange={handleProgressChange}
                className="w-full h-1 bg-gray-400 rounded-lg appearance-none cursor-pointer"
              />
              <span className="text-white text-xs">{formatTime(duration)}</span>
            </div>
            {/* Control Buttons */}
          </div>
        </div>
      </div>
    </>
  );
}
